import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Play, ShieldCheck, WifiOff, Sparkles } from "lucide-react";
import PhoneMockup from "@/components/PhoneMockup";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TRUST = [
  { icon: Sparkles, label: "AI-powered reading" },
  { icon: WifiOff, label: "Works offline" },
  { icon: ShieldCheck, label: "Private by design" },
];

export default function Hero() {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateY = useSpring(useTransform(mx, [0, 1], [7, -7]), { stiffness: 60, damping: 16 });
  const rotateX = useSpring(useTransform(my, [0, 1], [-6, 6]), { stiffness: 60, damping: 16 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pb-28"
      aria-label="Introduction to VoiceLens AI"
      onMouseMove={onMouseMove}
    >
      <div className="hero-grid pointer-events-none absolute inset-0" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#2563EB]/25 blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-[-10%] top-1/3 h-[420px] w-[420px] rounded-full bg-[#00D4FF]/12 blur-[120px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#00D4FF]/30 bg-[#0C2340]/70 px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-[#00D4FF] shadow-[0_0_10px_#00D4FF]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#00D4FF]">
              AI Reading Companion
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <RevealLine delay={0.25}>Let AI Read</RevealLine>
            <RevealLine delay={0.38}>
              the <span className="text-glow-gradient">World</span>
            </RevealLine>
            <RevealLine delay={0.51}>to You.</RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheadline"
          >
            VoiceLens AI helps people with low vision, dyslexia, seniors, and busy professionals
            instantly read, understand, and interact with printed and digital text — using the
            power of artificial intelligence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="/?view=app"
              data-testid="hero-cta-button"
              className="group inline-flex min-h-[56px] items-center gap-3 rounded-full bg-[#2563EB] px-8 py-4 text-base font-bold text-white shadow-[0_0_30px_rgba(37,99,235,0.5)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1D4ED8] hover:shadow-[0_0_44px_rgba(0,212,255,0.55)]"
            >
              Open VoiceLens AI
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </a>
            <a
              href="#showcase"
              data-testid="hero-watch-demo-button"
              className="group inline-flex min-h-[56px] items-center gap-3 rounded-full border border-[#00D4FF]/35 px-8 py-4 text-base font-semibold text-slate-100 transition-all duration-300 hover:border-[#00D4FF]/70 hover:bg-[#00D4FF]/10"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#00D4FF]/15 transition-colors duration-300 group-hover:bg-[#00D4FF]/30">
                <Play className="h-3.5 w-3.5 fill-[#00D4FF] text-[#00D4FF]" aria-hidden="true" />
              </span>
              Watch Demo
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
            aria-label="Product highlights"
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm text-slate-400">
                <Icon className="h-4 w-4 text-[#00D4FF]" aria-hidden="true" />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto [perspective:1200px]"
        >
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
            <PhoneMockup />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
